import { Fragment, useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableContainer from '@mui/material/TableContainer';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import RefreshIcon from '@mui/icons-material/Refresh';
import TableViewIcon from '@mui/icons-material/TableView';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HistoryIcon from '@mui/icons-material/History';
import TuneIcon from '@mui/icons-material/Tune';
import SyncIcon from '@mui/icons-material/Sync';
import { DialogClose } from '@/components/DialogClose';
import {
  requestDiscovery, useAttendanceBackfill, useBulkEnable, useHrmsCatalog,
  useHrmsSyncStatus, useSaveConnection, useStartAttendanceBackfill, useStartSync, useSyncRuns,
  useUpdateSource,
  type AttendanceBackfill, type Catalog, type SyncSource } from '@/api/hrms-sync';
import { errorMessage } from '@/lib/api-client';
import { EmployeeFieldMapPanel } from './EmployeeFieldMapPanel';
import { RunProgressPanel } from './RunProgressPanel';
import { SourceMappingPanel } from './SourceMappingPanel';
import { StagedDataDialog } from './StagedDataDialog';
import { fmtDay, fmtNum, fmtWhen, TYPE_LABEL } from './format';
import { MappingChip, RunStatusChip } from './shared';

type TabKey = 'sources' | 'mapping' | 'progress' | 'history';

const EMPLOYEE_MAP = '__employees__';

/**
 * "Sync uKnowva data" — for a workspace already connected to its uKnowva.
 *
 *  1. Sources  — what the instance exposes right now (standard masters, custom
 *                masters, tables, employees, attendance). New ones are flagged.
 *                Tick what to fetch.
 *  2. Mapping  — match each source's columns to a payroll master (shift, leave
 *                type, department, …), or keep it as stored-only data.
 *  3. Progress — the run, live: every source's status and every error, while
 *                the run carries on with what it can fetch.
 *  4. History  — earlier runs, their summaries and log files.
 */
export function HrmsSyncDialog({
  open, onClose, canEdit, initialRunId }: {
  open: boolean;
  onClose: () => void;
  canEdit: boolean;
  initialRunId?: string | null;
}) {
  const [tab, setTab] = useState<TabKey>('sources');
  const [refreshCount, setRefreshCount] = useState(0);
  const status = useHrmsSyncStatus(open);
  const connected = !!status.data?.connected;
  const catalog = useHrmsCatalog(open && connected);
  // What the last live discovery said. A registry-only refetch carries no
  // discovery news, so the banners are kept from the last real check.
  const [discovery, setDiscovery] = useState<Pick<Catalog, 'problems' | 'new_sources' | 'refreshed_at'> | null>(null);
  useEffect(() => {
    const c = catalog.data;
    if (c?.refreshed_at) {
      setDiscovery({ problems: c.problems, new_sources: c.new_sources, refreshed_at: c.refreshed_at });
    }
  }, [catalog.data]);
  const runs = useSyncRuns(open && connected && tab === 'history');
  const startSync = useStartSync();
  const bulk = useBulkEnable();
  const update = useUpdateSource();

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [selectionTouched, setSelectionTouched] = useState(false);
  const [mapKey, setMapKey] = useState<string | null>(null);
  const [runId, setRunId] = useState<string | null>(initialRunId ?? null);
  const [viewing, setViewing] = useState<SyncSource | null>(null);
  const [includeBlocked, setIncludeBlocked] = useState(false);
  const [attStart, setAttStart] = useState('');
  const [attEnd, setAttEnd] = useState('');
  const [filter, setFilter] = useState('');

  const sources = useMemo(() => catalog.data?.sources ?? [], [catalog.data]);
  const kinds = useMemo(() => catalog.data?.kinds ?? [], [catalog.data]);
  const kindLabel = (k: string | null | undefined) =>
    (k === 'employee' ? 'Employees' : k === 'attendance' ? 'Attendance days'
      : k === 'branding' ? 'Entity branding'
      : kinds.find((x) => x.key === k)?.label) ?? (k || '—');

  // Default selection: everything enabled and currently offered.
  useEffect(() => {
    if (!catalog.data || selectionTouched) return;
    setSelected(new Set(catalog.data.sources
      .filter((s) => s.is_enabled && s.is_available)
      .map((s) => s.source_key)));
  }, [catalog.data, selectionTouched]);

  // Jump straight to a run that is already going.
  useEffect(() => {
    if (!open) return;
    const active = initialRunId || status.data?.active_run_id;
    if (active) {
      setRunId(active);
      setTab('progress');
    }
  }, [open, initialRunId, status.data?.active_run_id]);

  useEffect(() => {
    if (!mapKey && sources.length) {
      const first = sources.find((s) => s.mapping_status === 'needs_review' || s.mapping_status === 'auto_ready');
      if (first) setMapKey(first.source_key);
    }
  }, [sources, mapKey]);

  const refresh = () => {
    setRefreshCount((n) => n + 1);
    requestDiscovery();
    void catalog.refetch();
    void status.refetch();
  };

  const toggle = (key: string) => {
    setSelectionTouched(true);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const visible = sources.filter((s) => {
    if (!filter.trim()) return true;
    const q = filter.toLowerCase();
    return s.title.toLowerCase().includes(q) || s.source_key.toLowerCase().includes(q)
      || (s.target_kind ?? '').includes(q);
  });
  const selectable = visible.filter((s) => s.is_available);
  const allChecked = selectable.length > 0 && selectable.every((s) => selected.has(s.source_key));

  const chosen = sources.filter((s) => selected.has(s.source_key) && s.is_available);
  const wantsAttendance = selected.has('core:attendance');
  const activeRun = status.data?.active_run_id;
  const attendanceOffered = sources.some((s) => s.source_key === 'core:attendance' && s.is_available);
  const backfill = useAttendanceBackfill(open && connected && attendanceOffered);
  const startBackfill = useStartAttendanceBackfill();

  const mapSource = sources.find((s) => s.source_key === mapKey) ?? null;
  // On the Column mapping tab the button syncs the source being mapped — the
  // one just edited — never the whole Sources selection.
  const usersSource = sources.find((s) => s.source_key === 'core:users' && s.is_available) ?? null;
  const mappedRunSource: SyncSource | null = mapKey === EMPLOYEE_MAP ? usersSource
    : mapSource?.is_available ? mapSource : null;
  const onMappingTab = tab === 'mapping';
  const runKeys = onMappingTab
    ? (mappedRunSource ? [mappedRunSource.source_key] : [])
    : chosen.map((s) => s.source_key);
  const runIncludesAttendance = runKeys.includes('core:attendance');

  const openRun = (id: string) => {
    setRunId(id);
    setTab('progress');
  };
  const openBusyRun = (err: unknown) => {
    const body = (err as { body?: { detail?: { run_id?: string } } }).body;
    if (body?.detail?.run_id) openRun(body.detail.run_id);
  };

  const start = async () => {
    try {
      const out = await startSync.mutateAsync({
        sources: runKeys,
        options: {
          include_blocked_users: includeBlocked,
          attendance_start: runIncludesAttendance && attStart ? attStart : null,
          attendance_end: runIncludesAttendance && attEnd ? attEnd : null } });
      openRun(out.run_id);
    } catch (err) {
      openBusyRun(err);
    }
  };

  const fetchOlderAttendance = async () => {
    try {
      openRun((await startBackfill.mutateAsync()).run_id);
    } catch (err) {
      openBusyRun(err);
    }
  };

  const mappable = sources.filter((s) => !s.is_core && s.is_available);
  const needsReview = sources.filter((s) => s.mapping_status === 'needs_review' && s.is_available).length;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg"
      slotProps={{ paper: { sx: { height: { md: '90vh' } } } }}>
      <DialogTitle sx={{ pr: 6 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
          <SyncIcon color="primary" />
          <span>Sync uKnowva data</span>
          {status.data?.instance && (
            <Chip size="small" variant="outlined" label={status.data.instance.replace(/^https?:\/\//, '')} />
          )}
          {connected && (
            <Tooltip title="Ask uKnowva again what it exposes">
              <span>
                <IconButton size="small" onClick={refresh} disabled={catalog.isFetching}>
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Stack>
        <DialogClose onClose={onClose} />
      </DialogTitle>

      {status.isLoading && <LinearProgress />}
      {status.error && (
        <DialogContent><Alert severity="error">{errorMessage(status.error)}</Alert></DialogContent>
      )}
      {status.data && !connected && (
        <DialogContent>
          <NotConnected reason={status.data.reason} mode={status.data.mode}
            instance={status.data.instance} canEdit={canEdit} />
        </DialogContent>
      )}

      {connected && (
        <>
          <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ px: 3, borderBottom: 1, borderColor: 'divider' }}>
            <Tab value="sources" label={`Sources${sources.length ? ` (${sources.length})` : ''}`} />
            <Tab value="mapping" label={
              <Stack direction="row" spacing={0.75} alignItems="center">
                <span>Column mapping</span>
                {needsReview > 0 && <Chip size="small" color="warning" label={needsReview} sx={{ height: 18 }} />}
              </Stack>
            } />
            <Tab value="progress" label={activeRun ? 'Progress ●' : 'Progress'} disabled={!runId} />
            <Tab value="history" label="History" />
          </Tabs>

          <DialogContent sx={{ pt: 2 }}>
            {tab === 'sources' && (
              <Box>
                {catalog.isFetching && <LinearProgress sx={{ mb: 1 }} />}
                {catalog.error && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(catalog.error)}</Alert>}
                {(discovery?.problems ?? []).map((p) => (
                  <Alert key={p.call} severity="error" sx={{ mb: 1 }}>
                    <b>{p.call}</b> failed. Showing what could still be read. {p.error}
                  </Alert>
                ))}
                {(discovery?.new_sources.length ?? 0) > 0 && (
                  <Alert severity="info" sx={{ mb: 1.5 }}>
                    {discovery!.new_sources.length} new source(s) found on uKnowva since the last check.
                    Each gets its own table in this workspace on its first sync.
                  </Alert>
                )}
                {catalog.data && !catalog.data.employee_field_map_confirmed && (
                  <Alert severity="warning" sx={{ mb: 1.5 }}
                    action={<Button color="inherit" size="small"
                      onClick={() => { setMapKey(EMPLOYEE_MAP); setTab('mapping'); }}>Map fields</Button>}>
                    The employee field mapping is not confirmed. Employees will be stored but not created
                    or updated in payroll.
                  </Alert>
                )}

                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <TextField size="small" placeholder="Filter sources" value={filter}
                    onChange={(e) => setFilter(e.target.value)} sx={{ width: 260 }} />
                  <Box sx={{ flex: 1 }} />
                  <Typography variant="caption" color="text.secondary">
                    {discovery?.refreshed_at ? `Checked ${fmtWhen(discovery.refreshed_at)} · ` : ''}
                    {refreshCount > 0 ? `refreshed ${refreshCount}×` : ''}
                  </Typography>
                </Stack>

                <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
                  <TableContainer><Table size="small" stickyHeader sx={{ '& td, & th': { py: 0.5 } }}>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox size="small" checked={allChecked}
                            indeterminate={!allChecked && selectable.some((s) => selected.has(s.source_key))}
                            onChange={() => {
                              setSelectionTouched(true);
                              setSelected(allChecked ? new Set() : new Set(selectable.map((s) => s.source_key)));
                            }} />
                        </TableCell>
                        <TableCell>Source</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Imports into</TableCell>
                        <TableCell>Mapping</TableCell>
                        <TableCell align="right">Stored rows</TableCell>
                        <TableCell>Last sync</TableCell>
                        <TableCell>
                          <Tooltip title="Included in automatic syncs (onboarding and scheduled)">
                            <span>Auto</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {visible.map((s) => (
                        <Fragment key={s.source_key}>
                        <TableRow hover
                          sx={!s.is_available ? { opacity: 0.55 } : undefined}>
                          <TableCell padding="checkbox">
                            <Checkbox size="small" disabled={!s.is_available}
                              checked={selected.has(s.source_key) && s.is_available}
                              onChange={() => toggle(s.source_key)} />
                          </TableCell>
                          <TableCell sx={{ minWidth: 220 }}>
                            <Stack direction="row" spacing={0.75} alignItems="center">
                              <Typography variant="body2" fontWeight={600}>{s.title}</Typography>
                              {s.is_new && <Chip size="small" color="info" label="new" sx={{ height: 18 }} />}
                              {!s.is_available && <Chip size="small" label="no longer offered" sx={{ height: 18 }} />}
                            </Stack>
                            <Typography variant="caption" color="text.secondary" sx={{}}>
                              {s.source_key} → {s.local_table}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">{TYPE_LABEL[s.source_type]}</Typography>
                          </TableCell>
                          <TableCell>
                            {s.is_core ? (
                              <Typography variant="caption" color="text.secondary">
                                {s.source_key === 'core:users' ? 'Employees' :
                                  s.source_key === 'core:attendance' ? 'Attendance (open periods)' :
                                  s.source_key === 'core:theme' ? 'Branding (every entity)' : '—'}
                              </Typography>
                            ) : (
                              <TextField select size="small" variant="standard" value={s.target_kind ?? ''}
                                disabled={!canEdit || update.isPending} sx={{ minWidth: 150 }}
                                slotProps={{ select: { native: true } }}
                                onChange={(e) => update.mutate({
                                  sourceKey: s.source_key, target_kind: e.target.value || null })}>
                                <option value="">Store only</option>
                                {kinds.map((k) => (
                                  <option key={k.key} value={k.key}>{k.label}</option>
                                ))}
                              </TextField>
                            )}
                          </TableCell>
                          <TableCell>
                            {s.is_core ? (
                              s.source_key === 'core:users' ? (
                                <Chip size="small"
                                  color={catalog.data?.employee_field_map_confirmed ? 'success' : 'warning'}
                                  label={catalog.data?.employee_field_map_confirmed ? 'Confirmed' : 'Needs mapping'}
                                  onClick={() => { setMapKey(EMPLOYEE_MAP); setTab('mapping'); }} />
                              ) : null
                            ) : (
                              <Box sx={{ cursor: 'pointer' }}
                                onClick={() => { setMapKey(s.source_key); setTab('mapping'); }}>
                                <MappingChip status={s.mapping_status} />
                              </Box>
                            )}
                          </TableCell>
                          <TableCell align="right">{fmtNum(s.staged_rows)}</TableCell>
                          <TableCell>
                            <Typography variant="caption" sx={{ display: 'block' }}>
                              {fmtWhen(s.last_synced_at)}
                            </Typography>
                            {s.last_status && (
                              <Tooltip title={s.last_error ?? ''}>
                                <Typography variant="caption"
                                  color={s.last_status === 'OK' ? 'success.main'
                                    : s.last_status === 'FAILED' ? 'error.main' : 'warning.main'}>
                                  {s.last_status}
                                </Typography>
                              </Tooltip>
                            )}
                          </TableCell>
                          <TableCell>
                            <Switch size="small" checked={s.is_enabled}
                              disabled={!canEdit || bulk.isPending}
                              onChange={(e) => bulk.mutate({
                                source_keys: [s.source_key], is_enabled: e.target.checked })} />
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {!s.is_core && (
                              <Tooltip title="Map columns">
                                <IconButton size="small"
                                  onClick={() => { setMapKey(s.source_key); setTab('mapping'); }}>
                                  <TuneIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="View stored data">
                              <span>
                                <IconButton size="small" disabled={!s.staged_rows}
                                  onClick={() => setViewing(s)}>
                                  <TableViewIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                        {s.source_key === 'core:attendance' && s.is_available && (
                          <OlderAttendanceRow
                            data={backfill.data} loading={backfill.isLoading}
                            error={backfill.error ? errorMessage(backfill.error) : null}
                            startError={startBackfill.error ? errorMessage(startBackfill.error) : null}
                            canEdit={canEdit} busy={!!activeRun || startBackfill.isPending}
                            starting={startBackfill.isPending}
                            onFetch={fetchOlderAttendance} onOpenRun={openRun} />
                        )}
                        </Fragment>
                      ))}
                      {!catalog.isLoading && visible.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={9}>
                            <Typography variant="body2" color="text.secondary">No sources match.</Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table></TableContainer>
                </Paper>
                {update.error && <Alert severity="error" sx={{ mt: 1 }}>{errorMessage(update.error)}</Alert>}

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2 }} alignItems={{ sm: 'center' }}>
                  {selected.has('core:users') && (
                    <Tooltip title="On: blocked uKnowva users are created and updated in payroll like everyone else. Off: they are not created. But once Last working date is mapped in the employee field mapping, employees already in payroll still get their last working date from them.">
                      <FormControlLabel
                        control={<Switch size="small" checked={includeBlocked}
                          onChange={(e) => setIncludeBlocked(e.target.checked)} />}
                        label={<Typography variant="body2">Include blocked users (leavers)</Typography>}
                      />
                    </Tooltip>
                  )}
                  {wantsAttendance && (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2">Attendance from</Typography>
                      <TextField type="date" size="small" value={attStart}
                        onChange={(e) => setAttStart(e.target.value)} />
                      <Typography variant="body2">to</Typography>
                      <TextField type="date" size="small" value={attEnd}
                        onChange={(e) => setAttEnd(e.target.value)} />
                      <Tooltip title="Read attendance from uKnowva for the dates chosen (up to 92 days; empty = the attendance cycle of the open period that owns today). Each day is written into the employee's own pay group's period whose attendance cycle covers it. OPEN periods only; days in a locked or processed period, or for a uKnowva user with no payroll employee, are counted in the result, never written.">
                        <Chip size="small" variant="outlined" label="?" />
                      </Tooltip>
                    </Stack>
                  )}
                </Stack>
              </Box>
            )}

            {tab === 'mapping' && (
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="stretch">
                <Paper variant="outlined" sx={{ width: { md: 280 }, flexShrink: 0, maxHeight: '65vh', overflowY: 'auto' }}>
                  <List dense disablePadding
                    subheader={<ListSubheader>Employees</ListSubheader>}>
                    <ListItemButton selected={mapKey === EMPLOYEE_MAP} onClick={() => setMapKey(EMPLOYEE_MAP)}>
                      <ListItemText primary="Employee profile fields"
                        secondary={catalog.data?.employee_field_map_confirmed ? 'confirmed' : 'needs mapping'} />
                    </ListItemButton>
                  </List>
                  <List dense disablePadding subheader={<ListSubheader>Masters & tables</ListSubheader>}>
                    {mappable.map((s) => (
                      <ListItemButton key={s.source_key} selected={mapKey === s.source_key}
                        onClick={() => setMapKey(s.source_key)}>
                        <ListItemText
                          primary={s.title}
                          secondary={s.target_kind ? `${kindLabel(s.target_kind)} · ${s.mapping_status.replace('_', ' ')}` : 'store only'}
                          secondaryTypographyProps={{
                            color: s.mapping_status === 'needs_review' ? 'warning.main'
                              : s.mapping_status === 'confirmed' ? 'success.main' : 'text.secondary' }}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                </Paper>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {mapKey === EMPLOYEE_MAP && <EmployeeFieldMapPanel canEdit={canEdit} />}
                  {mapSource && (
                    <>
                      <Typography variant="h6">{mapSource.title}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                        {mapSource.source_key} · {mapSource.field_count} column(s) · stored in {mapSource.local_table}
                      </Typography>
                      <SourceMappingPanel key={mapSource.source_key} source={mapSource} kinds={kinds} canEdit={canEdit} />
                    </>
                  )}
                  {!mapKey && (
                    <Typography color="text.secondary">Pick a source on the left.</Typography>
                  )}
                </Box>
              </Stack>
            )}

            {tab === 'progress' && runId && (
              <RunProgressPanel runId={runId} canEdit={canEdit} kindLabel={kindLabel} />
            )}

            {tab === 'history' && (
              <Box>
                {runs.isLoading && <LinearProgress />}
                {runs.error && <Alert severity="error">{errorMessage(runs.error)}</Alert>}
                <TableContainer><Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Started</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>By</TableCell>
                      <TableCell>Summary</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(runs.data?.runs ?? []).map((r) => (
                      <TableRow key={r.id} hover>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtWhen(r.started_at || r.created_at)}</TableCell>
                        <TableCell><RunStatusChip status={r.status} /></TableCell>
                        <TableCell>
                          <Typography variant="caption">{r.trigger.toLowerCase()}</Typography>
                          {r.triggered_by && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              {r.triggered_by}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 480 }}>
                          <Typography variant="body2">{r.summary ?? '—'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Button size="small" onClick={() => { setRunId(r.id); setTab('progress'); }}>
                            Details
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {runs.data && runs.data.runs.length === 0 && (
                      <TableRow><TableCell colSpan={5}>
                        <Typography variant="body2" color="text.secondary">No syncs yet.</Typography>
                      </TableCell></TableRow>
                    )}
                  </TableBody>
                </Table></TableContainer>
              </Box>
            )}
          </DialogContent>

          <DialogActions sx={{ px: 3, py: 1.5 }}>
            {startSync.error && (
              <Alert severity="error" sx={{ flex: 1, py: 0 }}>{errorMessage(startSync.error)}</Alert>
            )}
            {tab !== 'progress' && canEdit && (
              <>
                <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                  {!onMappingTab ? `${chosen.length} source(s) selected`
                    : mappedRunSource ? `Syncs only “${mappedRunSource.title}”. Other sources are left alone`
                    : 'Pick a source on the left to sync it'}
                </Typography>
                <Button variant="contained" startIcon={<SyncIcon />}
                  disabled={!runKeys.length || startSync.isPending || !!activeRun || catalog.isLoading}
                  onClick={start}>
                  {activeRun ? 'A sync is running' : startSync.isPending ? 'Starting…'
                    : onMappingTab ? 'Sync this source' : 'Start sync'}
                </Button>
              </>
            )}
            {tab === 'progress' && (
              <Button onClick={() => setTab('sources')}>Back to sources</Button>
            )}
            {!canEdit && (
              <Typography variant="caption" color="text.secondary">
                Only administrators and HR managers can start a sync or change a mapping.
              </Typography>
            )}
          </DialogActions>
        </>
      )}

      <StagedDataDialog source={viewing} onClose={() => setViewing(null)} />
      {!connected && !status.isLoading && (
        <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
      )}
    </Dialog>
  );
}

/** Why the last older-attendance fetch did not count as done. */
function attemptReason(a: NonNullable<AttendanceBackfill['last_attempt']>): string {
  if (a.status === 'FAILED') return 'uKnowva could not be read';
  if (a.status === 'PARTIAL') return 'uKnowva was only partly read';
  switch (a.promote_status) {
    case 'NEEDS_MAPPING': return 'the days were stored, but the employee field mapping is not confirmed';
    case 'BLOCKED': return 'the days were stored, but the attendance day-code mapping is not usable';
    case 'SKIPPED': return 'no day matched a payroll employee in an open period';
    case 'FAILED': return 'the import into payroll failed';
    case 'STORED_ONLY': return 'the days were stored only, not imported';
    default: return 'it did not finish';
  }
}

const SHOW_PERIODS = 6;

/**
 * The "Older attendance" row under Attendance on the Sources tab.
 *
 * A sync with no dates reads only the current attendance cycle. This row
 * lists the earlier OPEN periods whose attendance never fully reached payroll
 * and fetches them a slice (≤ 92 days, newest first) per click, until every
 * one is in — then it stays, disabled, saying so.
 */
function OlderAttendanceRow({
  data, loading, error, startError, canEdit, busy, starting, onFetch, onOpenRun }: {
  data: AttendanceBackfill | undefined;
  loading: boolean;
  error: string | null;
  startError: string | null;
  canEdit: boolean;
  busy: boolean;
  starting: boolean;
  onFetch: () => void;
  onOpenRun: (runId: string) => void;
}) {
  const owed = data?.owed ?? [];
  const done = !!data?.done;
  const nxt = data?.next ?? null;
  const attempt = data?.last_attempt ?? null;

  let body: React.ReactNode;
  if (loading) {
    body = <Typography variant="caption" color="text.secondary">Checking earlier months…</Typography>;
  } else if (error) {
    body = <Typography variant="caption" color="error.main">Could not check earlier months: {error}</Typography>;
  } else if (done) {
    body = (
      <Stack direction="row" spacing={0.75} alignItems="center">
        <CheckCircleIcon fontSize="small" color="success" />
        <Typography variant="caption" color="text.secondary">
          {data?.older_open_periods
            ? 'All earlier open periods have their attendance in payroll.'
            : 'No earlier open periods are waiting for attendance.'}
        </Typography>
      </Stack>
    );
  } else {
    body = (
      <Box>
        <Typography variant="caption" sx={{ display: 'block' }}>
          {owed.length} earlier period(s) not yet in payroll:
        </Typography>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ my: 0.5 }}>
          {owed.slice(0, SHOW_PERIODS).map((p) => (
            <Tooltip key={p.calendar_id} title={`Attendance cycle ${fmtDay(p.start)} – ${fmtDay(p.end)}`}>
              <Chip size="small" variant="outlined" label={p.label} sx={{ height: 20 }} />
            </Tooltip>
          ))}
          {owed.length > SHOW_PERIODS && (
            <Chip size="small" label={`+${owed.length - SHOW_PERIODS} more`} sx={{ height: 20 }} />
          )}
        </Stack>
        {nxt && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            Next fetch reads {fmtDay(nxt.start)} – {fmtDay(nxt.end)}
            {owed.length > nxt.periods.length ? ' (up to 92 days a fetch. The row stays until every period is in)' : ''}.
          </Typography>
        )}
        {attempt && (
          <Typography variant="caption" color="warning.main" sx={{ display: 'block' }}>
            Last try ({fmtWhen(attempt.at)}, {fmtDay(attempt.start)} – {fmtDay(attempt.end)}): {attemptReason(attempt)}.{' '}
            <Box component="span" sx={{ textDecoration: 'underline', cursor: 'pointer' }}
              onClick={() => onOpenRun(attempt.run_id)}>Details</Box>
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <TableRow sx={{ bgcolor: 'action.hover' }}>
      <TableCell padding="checkbox" sx={{ textAlign: 'center' }}>
        <HistoryIcon fontSize="small" color={done ? 'disabled' : 'action'} />
      </TableCell>
      <TableCell sx={{ minWidth: 220 }}>
        <Typography variant="body2" fontWeight={600}>Older attendance</Typography>
        <Typography variant="caption" color="text.secondary">
          earlier months the current-cycle sync does not read
        </Typography>
      </TableCell>
      <TableCell colSpan={5}>
        {body}
        {startError && (
          <Typography variant="caption" color="error.main" sx={{ display: 'block' }}>{startError}</Typography>
        )}
      </TableCell>
      <TableCell colSpan={2} sx={{ whiteSpace: 'nowrap' }}>
        {canEdit && (
          <Tooltip title={done ? '' : 'Reads uKnowva for these dates and writes each day into its own OPEN period: the same as an attendance sync with dates. uKnowva’s day status replaces what those periods hold now; locked and processed periods are never written.'}>
            <span>
              <Button size="small" variant={done ? 'text' : 'outlined'}
                startIcon={done ? <CheckCircleIcon /> : <SyncIcon />}
                disabled={done || loading || !!error || !nxt || busy}
                onClick={onFetch}>
                {done ? 'Fetched' : starting ? 'Starting…' : 'Fetch older'}
              </Button>
            </span>
          </Tooltip>
        )}
      </TableCell>
    </TableRow>
  );
}

function NotConnected({
  reason, mode, instance, canEdit }: { reason: string | null; mode: 'multi' | 'single'; instance: string | null; canEdit: boolean }) {
  const save = useSaveConnection();
  const [url, setUrl] = useState(instance ?? '');
  const [token, setToken] = useState('');

  return (
    <Box>
      <Alert severity="warning" sx={{ mb: 2 }}>
        This workspace cannot sync yet: {reason ?? 'it is not connected to uKnowva.'}
      </Alert>
      {mode === 'multi' ? (
        <Typography variant="body2" color="text.secondary">
          The uKnowva API token for this workspace is managed by the payroll platform team. Ask them to
          store it (platform console → workspace → secrets), then reopen this window.
        </Typography>
      ) : canEdit ? (
        <Stack spacing={2} sx={{ maxWidth: 520 }}>
          <TextField label="uKnowva instance URL" size="small" value={url}
            onChange={(e) => setUrl(e.target.value)} placeholder="https://yourcompany.uknowva.com" />
          <TextField label="API token" size="small" type="password" value={token}
            onChange={(e) => setToken(e.target.value)}
            helperText="uKnowva → Admin → Manage API Tokens → Payroll APIs. Stored encrypted." />
          {save.error && <Alert severity="error">{errorMessage(save.error)}</Alert>}
          <Box>
            <Button variant="contained" disabled={!url.trim() || save.isPending}
              onClick={() => save.mutate({ instance_url: url.trim(), api_token: token.trim() || null })}>
              {save.isPending ? 'Saving…' : 'Connect'}
            </Button>
          </Box>
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary">
          Ask an administrator to connect this workspace.
        </Typography>
      )}
    </Box>
  );
}
